import type { AssetStatus } from "@hydrologiq/gql-experience/graphql"
import type { ElectrolyserStack } from "@hydrologiq/shared/figures/headlines/electrolyserStack"
import type { SystemViewData, SystemNode } from "../types/SystemView"

import { toAssetStatus } from "./systemTransform"

export interface ElectrolyserSummary {
  id: string
  name: string
  /** The asset's status (STANDBY / ERROR / AVAILABLE / …), or null if unrecognised. */
  status: AssetStatus | null
  /** Own flow rate (NL/h) if available as a live node attribute, else null. */
  flowRate: number | null
}

/**
 * Collect the electrolysers in a system for the per-electrolyser "Electrolyser Status" cards
 * (Card 2 — one card per electrolyser). Reads the system-view nodes directly (frontend),
 * matching on asset class `electrolyser`.
 *
 * NOTE: `class` isn't in the typed `AssetInfo` but is present at runtime (see SystemMonitoring),
 * hence the `as any`. Per-electrolyser flow comes from a live `flow_rate` node attribute when
 * present (only when integration_management is enabled); otherwise null.
 *
 * Status comes from the ASSET, not from the node's `operationalStatus`. The latter is read from the
 * `system_node_status` table, which nothing in core ever writes — so it is `"unknown"` for every
 * node of every system, which is why these cards all showed "Unknown". Verified against Burnaston
 * (live): all 21 nodes report `operationalStatus: "unknown"` while the three electrolysers'
 * `asset.status` correctly reads ERROR / STANDBY / STANDBY.
 */
export function collectElectrolysers(data: SystemViewData | undefined): ElectrolyserSummary[] {
  if (!data) return []
  const nodes: SystemNode[] = [
    ...(data.installations ?? []).flatMap((group) => group.nodes ?? []),
    ...(data.isolatedNodes ?? []),
  ]
  return nodes
    .filter((node) => ((node.asset as any)?.class ?? "").toLowerCase() === "electrolyser")
    .map((node) => {
      const flowAttr = (node.attributes ?? []).find((a) => a.field === "flow_rate")
      return {
        id: node.id,
        name: node.name,
        status: toAssetStatus(node.asset?.status),
        flowRate: flowAttr?.liveValue ?? null,
      }
    })
}

/**
 * Group the system's electrolysers into stacks, for the "Electrolyser Stack" summary card.
 *
 * A stack is a single system-view node whose asset is an electrolyser and which carries
 * `childAssets` — that is how the parent:child relationship actually reaches the frontend. Verified
 * against staging system 1cb4a857 ("Yoda"): one node for the parent, three entries in childAssets,
 * and the children do NOT appear as nodes of their own.
 *
 * Deliberately not using getSystemAssetGroups: it returns the same relationship but its
 * `AssetGroupChildType` carries only `monitoringStatus`, and it costs an extra query for structure
 * the system view already provides.
 *
 * What each unit can contribute:
 * - the parent has a real `asset.status` and the live `flow_rate`. That flow is already the group
 *   total — the monitoring instance aggregates flow_rate with `sum` — which is what makes the card's
 *   combined rate correct even though the children report nothing here.
 * - the children carry `status` (TODO) but no flow of their own — the
 *   parent's summed reading already covers the group. A child whose status is missing or
 *   unrecognised enters the roll-up as `null`, and `deriveStackStatus` ignores unknown statuses
 *   rather than letting absent data outvote the units it can see.
 *
 * Only electrolyser children are counted, so a mixed group can't drag non-electrolysers into the
 * roll-up.
 */
export function collectElectrolyserStacks(data: SystemViewData | undefined): ElectrolyserStack[] {
  if (!data) return []

  const nodes: SystemNode[] = [
    ...(data.installations ?? []).flatMap((group) => group.nodes ?? []),
    ...(data.isolatedNodes ?? []),
  ]

  return nodes
    .filter(
      (node) =>
        ((node.asset as any)?.class ?? "").toLowerCase() === "electrolyser" &&
        (node.childAssets?.length ?? 0) > 0,
    )
    .map((node) => {
      const flowAttr = (node.attributes ?? []).find((a) => a.field === "flow_rate")
      const children = (node.childAssets ?? []).filter(
        (child) => (child.assetClass ?? "").toLowerCase() === "electrolyser",
      )
      return {
        parentAssetId: node.asset?.id ?? node.id,
        parentAssetName: node.asset?.name ?? node.name,
        units: [
          {
            id: node.asset?.id ?? node.id,
            name: node.asset?.name ?? node.name,
            status: toAssetStatus(node.asset?.status),
            flowRate: flowAttr?.liveValue ?? null,
          },
          ...children.map((child) => ({
            id: child.id,
            name: child.name,
            status: toAssetStatus(child.status),
            flowRate: null,
          })),
        ],
      }
    })
}

/**
 * Derive the single AssetStatus for a system node.
 * Returns the node's asset status reported by the backend.
 */
export function getNodeAssetStatus(node: SystemNode): AssetStatus | null {
  return toAssetStatus(node.asset?.status)
}

